// app/utils/authcontext.tsx
'use client';

import { createContext, useContext, useState, ReactNode, useRef } from 'react';
import Cookies from 'js-cookie';
import * as Global from '@utils/global';
import { usePathname, useRouter } from 'next/navigation';
import { useEffect } from "react";
import axios from "axios";

// ===============================
// 型定義
// ===============================
interface AuthContextProps {
  domainTitle: string | null;
  authToken: string | null;
  authTokenExpiresAt: string | null;
  employeeGUID: string | null;
  employeeDomain: string | null;
  employeeName: string | null;
  employeeFlag: string | null;
  setDomainTitle: (domain_title: string | null) => void;
  setAuthToken: (token: string | null) => void;
  setAuthTokenExpiresAt: (expires_at: string | null) => void;
  setEmployeeGUID: (user_guid: string | null) => void;
  setEmployeeDomain: (domain: string | null) => void;
  setEmployeeName: (user_name: string | null) => void;
  setEmployeeFlag: (user_inner_flag: string | null) => void;
  loaded: boolean;
}

// ===============================
// コンテキスト本体
// ===============================
const AuthContext = createContext<AuthContextProps | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [domainTitle, setDomainTitle] = useState<string | null>(null);
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [authTokenExpiresAt, setAuthTokenExpiresAt] = useState<string | null>(null);
  const [employeeGUID, setEmployeeGUID] = useState<string | null>(null);
  const [employeeDomain, setEmployeeDomain] = useState<string | null>(null);
  const [employeeName, setEmployeeName] = useState<string | null>(null);
  const [employeeFlag, setEmployeeFlag] = useState<string | null>(null);
  const [loaded, setLoaded] = useState(false);
  const sessionSyncingRef = useRef(false);
  const sessionCookieRef = useRef<{ token: string | null; domain: string | null }>({ token: null, domain: null });
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const domain_title = Cookies.get(Global.COOKIE_DOMAIN_TITLE) ?? null;
    const token = Cookies.get(Global.COOKIE_AUTH_TOKEN) ?? null;
    const token_expires_at = Cookies.get(Global.COOKIE_AUTH_TOKEN_EXPIRES_AT) ?? null;
    const domain = Cookies.get(Global.COOKIE_EMPLOYEE_DOMAIN) ?? null;
    const user_name = Cookies.get(Global.COOKIE_EMPLOYEE_NAME) ?? null;
    const user_inner_flag = Cookies.get(Global.COOKIE_EMPLOYEE_FLAG) ?? null;

    if (token && domain) {
      sessionCookieRef.current = { token, domain };
      setDomainTitle(domain_title);
      setAuthToken(token);
      setAuthTokenExpiresAt(token_expires_at);
      setEmployeeDomain(domain);
      setEmployeeName(user_name);
      setEmployeeFlag(user_inner_flag);
    }

    setLoaded(true);
  }, []);

  useEffect(() => {
    if (!loaded || pathname === "/signout") return;
    const syncSessionFromCookies = async () => {
      if (sessionSyncingRef.current) return;
      const cookieToken = Cookies.get(Global.COOKIE_AUTH_TOKEN) ?? null;
      const cookieTokenExpiresAt = Cookies.get(Global.COOKIE_AUTH_TOKEN_EXPIRES_AT) ?? null;
      const cookieDomain = Cookies.get(Global.COOKIE_EMPLOYEE_DOMAIN) ?? null;
      const cookieDomainTitle = Cookies.get(Global.COOKIE_DOMAIN_TITLE) ?? null;
      const cookieName = Cookies.get(Global.COOKIE_EMPLOYEE_NAME) ?? null;
      const cookieFlag = Cookies.get(Global.COOKIE_EMPLOYEE_FLAG) ?? null;
      const domainChanged = cookieDomain !== sessionCookieRef.current.domain;
      const tokenChanged = cookieToken !== sessionCookieRef.current.token;
      const cookieChanged = cookieToken !== sessionCookieRef.current.token || cookieDomain !== sessionCookieRef.current.domain;
      if (!cookieChanged) return;
      if (!cookieToken || !cookieDomain) {
        const signoutInProgress = sessionStorage.getItem(Global.SESSION_SIGNOUT_IN_PROGRESS) === "1";
        sessionStorage.removeItem(Global.SESSION_SIGNOUT_IN_PROGRESS);
        sessionCookieRef.current = { token: null, domain: null };
        setDomainTitle(null);
        setAuthToken(null);
        setAuthTokenExpiresAt(null);
        setEmployeeGUID(null);
        setEmployeeDomain(null);
        setEmployeeName(null);
        setEmployeeFlag(null);
        sessionStorage.removeItem("employeeVerified");
        router.replace(signoutInProgress ? "/signon" : "/signon?reason=session_expired");
        return;
      }
      sessionSyncingRef.current = true;
      try {
        const res = await axios.post<any>(`${Global.getApiBaseUrl()}/personal/me`, {
          base_token_interface: {
            token: cookieToken,
            user_domain: cookieDomain,
            ip_address: window.location.hostname,
            user_agent: navigator.userAgent,
          }
        });
        if (res.data?.auth_token) {
          Global.updateAuthTokenCookie(res.data.auth_token);
        }
        const syncedToken = res.data?.auth_token?.token ?? cookieToken;
        const syncedDomain = res.data?.user_domain ?? cookieDomain;
        const userChanged = Boolean(employeeGUID && res.data?.user_guid && res.data.user_guid !== employeeGUID);
        sessionCookieRef.current = { token: syncedToken, domain: syncedDomain };
        if (tokenChanged) {
          setAuthToken(syncedToken);
          setAuthTokenExpiresAt(res.data?.auth_token?.token_expires_at ?? cookieTokenExpiresAt);
        }
        if (domainChanged || userChanged || pathname === "/signon") {
          setDomainTitle(res.data?.domain_title ?? cookieDomainTitle);
          setAuthToken(syncedToken);
          setAuthTokenExpiresAt(res.data?.auth_token?.token_expires_at ?? cookieTokenExpiresAt);
          setEmployeeGUID(res.data?.user_guid ?? null);
          setEmployeeDomain(syncedDomain);
          setEmployeeName(res.data?.user_name ?? cookieName);
          setEmployeeFlag(String(res.data?.user_inner_flag ?? cookieFlag ?? ""));
        }
        sessionStorage.setItem("employeeVerified", "1");
        if (domainChanged || userChanged || pathname === "/signon") {
          router.replace("/");
        }
      } catch {
        sessionCookieRef.current = { token: null, domain: null };
        setDomainTitle(null);
        setAuthToken(null);
        setAuthTokenExpiresAt(null);
        setEmployeeGUID(null);
        setEmployeeDomain(null);
        setEmployeeName(null);
        setEmployeeFlag(null);
        sessionStorage.removeItem("employeeVerified");
        router.replace("/signon?reason=session_expired");
      } finally {
        sessionSyncingRef.current = false;
      }
    };
    const intervalId = window.setInterval(syncSessionFromCookies, 1000);
    window.addEventListener(Global.SESSION_MISMATCH_EVENT, syncSessionFromCookies);
    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener(Global.SESSION_MISMATCH_EVENT, syncSessionFromCookies);
    };
  }, [loaded, pathname, router, employeeGUID]);

  // 🚨 ロード完了まで描画を止める（SSRの不整合防止）
  if (!loaded) return null;

  return (
    <AuthContext.Provider
      value={{
        domainTitle,
        setDomainTitle,
        authToken,
        setAuthToken,
        authTokenExpiresAt,
        setAuthTokenExpiresAt,
        employeeGUID,
        setEmployeeGUID,
        employeeDomain,
        setEmployeeDomain,
        employeeName,
        setEmployeeName,
        employeeFlag,
        setEmployeeFlag,
        loaded,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// ===============================
// useAuth(): 認証情報アクセス
// ===============================
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return {
    ...ctx,
    // 社員判定
    isEmployee: ctx.employeeFlag ,
  };
};

// ===============================
// useAuthRedirect(): 認証リダイレクト
// ===============================
export const useAuthRedirect = () => {
  const { 
    domainTitle,
    authToken,
    employeeGUID,
    employeeDomain, 
    setDomainTitle, 
    setAuthToken,
    setAuthTokenExpiresAt,
    setEmployeeDomain, 
    setEmployeeName, 
    setEmployeeGUID, 
    setEmployeeFlag 
  } = useAuth();
  const router = useRouter();

  useEffect(() => {
    const checkValidSession = async () => {
      // tokenなし → ログイン画面へ
      if (!authToken || !employeeDomain) {
        router.push("/signon");
        return;
      }

      // 🔒 一度検証済みならスキップ
      const verified = sessionStorage.getItem("employeeVerified");
      if (verified === "1" && employeeGUID) return;

      try {
        const currentToken = Global.getCurrentAuthToken(authToken);
        if (!currentToken) {
          throw new Error("認証トークンが取得できません");
        }
        const res = await axios.post<any>(`${Global.getApiBaseUrl()}/personal/me`, {
          base_token_interface: {
            token: currentToken,
            user_domain: employeeDomain,
            ip_address: window.location.hostname,
            user_agent: navigator.userAgent,
          }
        });

        if (res.data.user_name) {
          setDomainTitle(res.data.domain_title);
          setEmployeeGUID(res.data.user_guid);
          setEmployeeDomain(res.data.user_domain);
          setEmployeeName(res.data.user_name);
          setEmployeeFlag(res.data.user_inner_flag);
          sessionStorage.setItem("employeeVerified", "1");
        }
      } catch (e) {
        // 🔄 認証失敗 → ログアウト処理
        Cookies.remove(Global.COOKIE_DOMAIN_TITLE);
        Cookies.remove(Global.COOKIE_AUTH_TOKEN);
        Cookies.remove(Global.COOKIE_AUTH_TOKEN_EXPIRES_AT);
        Cookies.remove(Global.COOKIE_EMPLOYEE_DOMAIN);
        Cookies.remove(Global.COOKIE_EMPLOYEE_NAME);
        Cookies.remove(Global.COOKIE_EMPLOYEE_FLAG);
        setDomainTitle(null);
        setAuthToken(null);
        setAuthTokenExpiresAt(null);
        setEmployeeGUID(null);
        setEmployeeDomain(null);
        setEmployeeName(null);
        setEmployeeFlag(null);
        sessionStorage.removeItem("employeeVerified");
        router.push("/signon");
      }
    };

    checkValidSession();
  }, [authToken, employeeDomain, employeeGUID, router]);
};
